/**
 * Agent collection system - effect calculation, drop logic, slot count
 * Headless - no GUI
 */

import type { GameState, AgentCard } from '../state.js';
import type { BossId } from '../state.js';
import { AGENT_DEFS } from '../data/agents.js';
import type { AgentEffectType, AgentDef } from '../data/agents.js';

/**
 * Calculate multiplicative bonus from all equipped agents of a given effect type.
 * Returns 1.0 if no agents have that effect (no bonus).
 */
export function getEquippedAgentBonus(
  state: GameState,
  effectType: AgentEffectType,
): number {
  const ac = state.agentCollection;
  if (!ac) return 1.0;

  let mult = 1.0;
  for (const slotId of ac.equipped) {
    if (slotId === null) continue;
    const def = AGENT_DEFS.find(a => a.id === slotId);
    if (!def || def.effectType !== effectType) continue;
    mult *= def.effectValue;
  }
  return mult;
}

/**
 * Get the number of equip slots based on prestige progress.
 * Base 3, +1 at prestige 1, +1 at prestige2 1 = max 5.
 */
export function getEquipSlotCount(state: GameState): number {
  if (state.prestige2Count >= 1) return 5;
  if (state.prestigeCount >= 1) return 4;
  return 3;
}

/**
 * Roll for an agent drop from a defeated boss.
 * Checks all agents whose obtainCondition is boss_drop for the given bossId.
 * Returns the agentId if a drop occurs, or null.
 */
export function rollAgentDrop(bossId: BossId): string | null {
  const candidates = AGENT_DEFS.filter(
    a => a.obtainCondition.type === 'boss_drop'
      && a.obtainCondition.bossId === bossId,
  );

  for (const agent of candidates) {
    const cond = agent.obtainCondition as { type: 'boss_drop'; dropRate: number };
    if (Math.random() < cond.dropRate) {
      return agent.id;
    }
  }
  return null;
}

/**
 * Build the AgentCard entry for granting an agent.
 * If already owned, increments count.
 */
export function grantAgent(
  existing: Record<string, AgentCard>,
  agentId: string,
): Record<string, AgentCard> {
  const now = Date.now();
  const prev = existing[agentId];
  return {
    ...existing,
    [agentId]: prev
      ? { ...prev, count: prev.count + 1 }
      : { agentId, obtainedAt: now, count: 1 },
  };
}

/**
 * Get collected count (unique agent types).
 */
export function getCollectedUniqueCount(state: GameState): number {
  if (!state.agentCollection) return 0;
  return Object.keys(state.agentCollection.collected).length;
}

/**
 * Check if an agent is currently equipped in any slot.
 */
export function isAgentEquipped(state: GameState, agentId: string): boolean {
  if (!state.agentCollection) return false;
  return state.agentCollection.equipped.includes(agentId);
}

/**
 * Get an agent def safely, with type assertion for non-undefined.
 */
export function getAgentDefStrict(agentId: string): AgentDef | undefined {
  return AGENT_DEFS.find(a => a.id === agentId);
}
